import { useState } from 'react';
import { Plus, Edit3, Trash2, Star, ChevronDown, TrendingUp, TrendingDown, Coins } from 'lucide-react';
import { fmtDate, SECTION_COLOR, SECTION_BG } from '../receptura/receptUtils';

/**
 * Ceník položky Spižírny — **karta na dodavatele**, ne tabulka cen.
 *
 * Tabulka míchala do jednoho seznamu dvě různé otázky: „za kolik to teď od
 * koho beru" (to se čte pořád) a „jak se ta cena vyvíjela" (to se čte zřídka,
 * ale u jednoho konkrétního dodavatele). Řádky se přitom řadily podle data,
 * takže tři ceny jednoho dodavatele stály rozházené mezi cizími.
 *
 * Karta odpovídá první otázku rovnou: jeden dodavatel = jedna karta s **platnou
 * cenou**. Historie je pod ní schovaná pod rozbalovátkem a ukazuje se jen tam,
 * kde je co ukazovat (dodavatel má víc než jednu cenu).
 *
 * Stejné na mobilu i na desktopu — otázka je v obou případech táž, jen je na
 * desktopu víc místa. Rozdíl je v hustotě (odsazení, velikost cíle), ne ve
 * struktuře: dvě různá rozvržení by se rozešla u první změny.
 *
 * Změna ceny se počítá **proti předchozí ceně téhož dodavatele** — souběžná
 * cena jiného dodavatele není předchozí cena, jen jiná nabídka (stejné pravidlo
 * jako ve sloupci mřížky, viz `materialGrid.js`).
 */

/** Platná dnes = začala a ještě neskončila. Bez shody padá na nejnovější. */
const platnaDnes = (ceny) => {
    const dnes = new Date().toISOString().split('T')[0];
    const den = (v) => String(v ?? '').split('T')[0];
    return ceny.find(c => den(c.platnost_od) <= dnes && (!c.platnost_do || den(c.platnost_do) >= dnes)) ?? ceny[0];
};

/** Ceny jednoho dodavatele, od nejnovější — pořadí karty i historie. */
const odNejnovejsi = (ceny) => [...ceny].sort((a, b) => String(b.platnost_od ?? '').localeCompare(String(a.platnost_od ?? '')));

export function seskupitPodleDodavatele(ceny) {
    const mapa = new Map();
    for (const c of ceny) {
        const klic = c.dodavatel_id ?? `_${c.dodavatel?.nazev ?? 'bez'}`;
        if (!mapa.has(klic)) mapa.set(klic, []);
        mapa.get(klic).push(c);
    }
    const skupiny = [...mapa.entries()].map(([klic, jeho]) => {
        const serazene = odNejnovejsi(jeho);
        const aktualni = platnaDnes(serazene);
        const idx = serazene.indexOf(aktualni);
        const predchozi = serazene[idx + 1] ?? null;   // předchozí cena TÉHOŽ dodavatele
        return {
            klic,
            dodavatel: aktualni?.dodavatel ?? jeho[0]?.dodavatel ?? null,
            aktualni,
            predchozi,
            historie: serazene.filter(c => c !== aktualni),
            // Dvě různé otázky, které se nesmí slít do jedné:
            //
            // `jePrimarni` — je primární PRÁVĚ TA cena, co je na kartě vidět?
            //   Podle toho svítí hvězda, protože tu cenu taky přepíná. Kdyby
            //   svítila podle „některá cena dodavatele", klik na svítící hvězdu
            //   by tiše přehodil primární z jedné ceny na druhou.
            // `jePrimarniDodavatel` — má primární cenu kdekoli ve své historii?
            //   Podle toho se řadí, aby dodavatel, ze kterého počítá kalkulace,
            //   zůstal nahoře i ve chvíli, kdy jeho primární cenu přebila
            //   novější (což je přesně stav, který je potřeba si všimnout —
            //   hvězda je pak vidět v rozbalené historii).
            jePrimarni: !!aktualni?.je_primarni,
            jePrimarniDodavatel: jeho.some(c => c.je_primarni),
        };
    });
    return skupiny.sort((a, b) => {
        if (a.jePrimarniDodavatel !== b.jePrimarniDodavatel) return a.jePrimarniDodavatel ? -1 : 1;
        // Zbytek podle platné ceny vzestupně: kdo je levnější, je výš.
        return (Number(a.aktualni?.cena_za_jednotku) || 0) - (Number(b.aktualni?.cena_za_jednotku) || 0);
    });
}

export default function Cenik({
    ceny = [], jednotka, jednotkaId,
    onPridat, onUpravit, onSmazat, onTogglePrimarni,
    OdkazNaDodavatele,
    isMobile = false, isDark = false, mutedColor, secondaryColor, btnPrimary,
}) {
    const [rozbalene, setRozbalene] = useState(() => new Set());
    const zkr = jednotka?.zkratka;
    const hranice = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const jemna = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    const podklad = isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-base)';

    const skupiny = seskupitPodleDodavatele(ceny);
    const cil = isMobile ? 44 : 32;                 // dotykový cíl × myší cíl

    const prepni = (klic) => setRozbalene(p => {
        const n = new Set(p);
        n.has(klic) ? n.delete(klic) : n.add(klic);
        return n;
    });

    const ikonaAkce = (Ikona, titulek, onClick, barva) => (
        <button onClick={onClick} title={titulek} aria-label={titulek}
            style={{
                background: 'none', border: 'none', cursor: 'pointer', color: barva,
                width: cil, height: cil, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
            }}>
            <Ikona size={isMobile ? 16 : 14} />
        </button>
    );

    if (skupiny.length === 0) {
        return (
            <div>
                <div className="rounded-xl text-center" style={{ padding: '32px 16px', border: `1px dashed ${hranice}` }}>
                    <Coins size={26} style={{ color: mutedColor, opacity: 0.5, margin: '0 auto 8px' }} />
                    <p className="text-sm font-medium" style={{ color: secondaryColor }}>Zatím žádná cena</p>
                    <p className="text-xs mt-1" style={{ color: mutedColor }}>Bez ceny vstupuje surovina do kalkulace nulou.</p>
                </div>
                <button onClick={() => onPridat(null)}
                    style={{ ...btnPrimary, marginTop: 12, width: isMobile ? '100%' : undefined, justifyContent: 'center', minHeight: isMobile ? 44 : undefined }}>
                    <Plus size={14} /> Přidat cenu
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex flex-col gap-2">
                {skupiny.map(s => {
                    const c = s.aktualni;
                    const otevreno = rozbalene.has(s.klic);
                    const altJed = c?.cena_jednotka_id && c.cena_jednotka_id !== jednotkaId;

                    // Zdražení červeně, zlevnění zeleně — proti témuž dodavateli.
                    const nova = Number(c?.cena_za_jednotku);
                    const stara = Number(s.predchozi?.cena_za_jednotku);
                    const zmenaPct = s.predchozi && stara > 0 ? ((nova - stara) / stara) * 100 : null;
                    const Sipka = zmenaPct > 0 ? TrendingUp : TrendingDown;
                    const barvaZmeny = zmenaPct > 0 ? '#EF4444' : '#10B981';

                    return (
                        <div key={s.klic} className="rounded-xl overflow-hidden"
                            style={{ border: `1px solid ${s.jePrimarniDodavatel ? SECTION_COLOR + '55' : hranice}`, background: podklad }}>

                            {/* Kdo — hvězda je zároveň přepínač primárního dodavatele */}
                            <div className="flex items-center gap-1" style={{ padding: isMobile ? '4px 6px 0 6px' : '6px 8px 0 8px' }}>
                                <button onClick={() => onTogglePrimarni(c.id)}
                                    title={s.jePrimarni ? 'Primární dodavatel' : 'Označit jako primárního'}
                                    aria-label={s.jePrimarni ? 'Primární dodavatel' : 'Označit jako primárního'}
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: s.jePrimarni ? SECTION_COLOR : mutedColor,
                                        width: cil, height: cil, flexShrink: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        WebkitTapHighlightColor: 'transparent',
                                    }}>
                                    <Star size={isMobile ? 17 : 15} fill={s.jePrimarni ? SECTION_COLOR : 'none'} />
                                </button>
                                <div className="min-w-0 flex-1 text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                                    {OdkazNaDodavatele ? <OdkazNaDodavatele cena={c} /> : (s.dodavatel?.nazev ?? '—')}
                                </div>
                                {s.jePrimarniDodavatel && (
                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0"
                                        style={{ background: SECTION_BG, color: SECTION_COLOR }}
                                        title={s.jePrimarni ? 'Z této ceny počítá kalkulace'
                                            : 'Primární cena tohoto dodavatele je v historii — přebila ji novější'}>
                                        prim.{!s.jePrimarni && <span style={{ fontWeight: 400 }}> ↓</span>}
                                    </span>
                                )}
                                {ikonaAkce(Edit3, 'Upravit cenu', () => onUpravit(c), secondaryColor)}
                                {ikonaAkce(Trash2, 'Smazat cenu', () => onSmazat(c.id), '#EF4444')}
                            </div>

                            {/* Za kolik — hlavní číslo karty */}
                            <div style={{ padding: isMobile ? `0 12px 10px ${6 + cil}px` : `0 12px 10px ${8 + cil}px` }}>
                                <div className="flex items-baseline gap-2 flex-wrap">
                                    <span style={{ fontSize: isMobile ? 21 : 19, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                                        {c ? Number(c.cena_za_jednotku).toFixed(2) : '—'}
                                    </span>
                                    <span className="text-xs" style={{ color: secondaryColor }}>Kč/{zkr} bez DPH</span>
                                    {zmenaPct != null && (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                            style={{ background: `${barvaZmeny}1f`, color: barvaZmeny }}
                                            title={`Předchozí cena ${stara.toFixed(2)} Kč od ${fmtDate(s.predchozi.platnost_od)}`}>
                                            <Sipka size={10} />{zmenaPct > 0 ? '+' : ''}{zmenaPct.toFixed(1)} %
                                        </span>
                                    )}
                                </div>
                                <div className="text-[11px] mt-1" style={{ color: mutedColor }}>
                                    DPH {c?.dph_sazba} % · s DPH {c ? Number(c.cena_s_dph_za_jednotku).toFixed(2) : '—'} Kč/{zkr}
                                    {altJed && <> · zadáno {Number(c.cena_bez_dph).toFixed(2)} Kč/{c.cena_jednotka?.zkratka}</>}
                                </div>
                                <div className="text-[11px] mt-0.5" style={{ color: mutedColor }}>
                                    platí od {fmtDate(c?.platnost_od)}{c?.platnost_do ? ` do ${fmtDate(c.platnost_do)}` : ''}
                                </div>
                            </div>

                            {/* Historie — jen když je co rozbalit */}
                            {s.historie.length > 0 && (
                                <>
                                    <button onClick={() => prepni(s.klic)}
                                        aria-expanded={otevreno}
                                        className="flex items-center gap-1.5 w-full text-[11px] font-medium"
                                        style={{
                                            padding: isMobile ? '0 12px' : '0 10px', minHeight: isMobile ? 44 : 32,
                                            background: 'none', border: 'none', borderTop: `1px solid ${jemna}`,
                                            color: secondaryColor, cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
                                        }}>
                                        <ChevronDown size={13} style={{ transform: otevreno ? 'rotate(180deg)' : 'none', transition: 'transform 0.18s' }} />
                                        Historie cen ({s.historie.length})
                                    </button>

                                    {otevreno && (
                                        <div style={{ borderTop: `1px solid ${jemna}` }}>
                                            {s.historie.map(h => (
                                                <div key={h.id} className="flex items-center gap-2"
                                                    style={{ padding: isMobile ? '0 6px 0 12px' : '0 8px 0 12px', minHeight: isMobile ? 44 : 34, borderTop: `1px solid ${jemna}` }}>
                                                    {/* Hvězda i tady — primární může být i cena, která už
                                                        nemá platnost (typicky když někdo zapomněl přepnout). */}
                                                    {h.je_primarni && (
                                                        <Star size={12} fill={SECTION_COLOR} style={{ color: SECTION_COLOR, flexShrink: 0 }} />
                                                    )}
                                                    <span className="text-[13px] font-semibold flex-shrink-0" style={{ color: 'var(--text-primary)' }}>
                                                        {Number(h.cena_za_jednotku).toFixed(2)}
                                                    </span>
                                                    <span className="text-[10px] flex-shrink-0" style={{ color: mutedColor }}>Kč/{zkr}</span>
                                                    <span className="text-[11px] min-w-0 flex-1 truncate" style={{ color: mutedColor }}>
                                                        {fmtDate(h.platnost_od)}{h.platnost_do ? ` – ${fmtDate(h.platnost_do)}` : ''}
                                                    </span>
                                                    {ikonaAkce(Edit3, 'Upravit cenu', () => onUpravit(h), secondaryColor)}
                                                    {ikonaAkce(Trash2, 'Smazat cenu', () => onSmazat(h.id), '#EF4444')}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            <button onClick={() => onPridat(null)}
                style={{ ...btnPrimary, marginTop: 12, width: isMobile ? '100%' : undefined, justifyContent: 'center', minHeight: isMobile ? 44 : undefined }}>
                <Plus size={14} /> Přidat cenu
            </button>
        </div>
    );
}
